import React, { useEffect, useState, useCallback } from 'react';
import { Box, Heading, useColorModeValue } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import LoadingSpinner from 'components/table/LoadingSpinner';
import PaginationControls from 'components/table/PaginationControls';

import InventoriesTable from './components/InventoriesTable';
import SummaryStats from './components/SummaryStats';
import FiltersSection from './components/FiltersSection';
import { useColors } from './components/colors';

import {
  getInventoriesApi,
  getInventoriesOverviewApi,
} from 'features/inventories/inventories.api';

const Inventories = () => {
  const dispatch = useDispatch();
  const { inventories, totalPages, currentPage, getInventoriesStatus } = useSelector(
    (state) => state.inventories,
  );

  const [searchValue, setSearchValue] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [selectedStatuses, setSelectedStatuses] = useState([]);

  const { backgroundCardColor } = useColors();

  useEffect(() => {
    dispatch(getInventoriesOverviewApi());
  }, [dispatch]);

  const fetchInventories = useCallback(
    (page = currentPage) => {
      dispatch(
        getInventoriesApi({
          page,
          limit: 10,
          search: searchValue,
          supplier: selectedSupplier,
          status: selectedStatuses.length > 0 ? selectedStatuses.join(',') : undefined,
        }),
      );
    },
    [dispatch, currentPage, searchValue, selectedSupplier, selectedStatuses],
  );

  useEffect(() => {
    fetchInventories(currentPage);
  }, [fetchInventories, currentPage, searchValue, selectedSupplier, selectedStatuses]);

  const handlePageChange = (page) => {
    fetchInventories(page);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleSearchSubmit = (value) => {
    setSearchValue(value);
    dispatch(
      getInventoriesApi({
        page: 1,
        limit: 10,
        search: value,
        supplier: selectedSupplier,
        status: selectedStatuses.length > 0 ? selectedStatuses.join(',') : undefined,
      }),
    );
  };

  const handleSupplierChange = (value) => {
    setSelectedSupplier(value);
  };

  const handleStatusChange = (values) => {
    setSelectedStatuses(values);
  };

  const handleRefresh = () => {
    fetchInventories(currentPage);
  };

  return (
    <Box px={{ base: 4, md: 10 }} py={{ base: 6, md: 8 }} mt="50px">
      <Heading as="h1" size="xl" mb={4}>
        Overview
      </Heading>
      <SummaryStats />
      <FiltersSection
        searchValue={searchValue}
        selectedSupplier={selectedSupplier}
        selectedStatuses={selectedStatuses}
        onSearch={handleSearch}
        onSearchSubmit={handleSearchSubmit}
        onSupplierChange={handleSupplierChange}
        onStatusChange={handleStatusChange}
      />
      <Box bg={backgroundCardColor} borderRadius="lg" boxShadow="sm" p={4}>
        {getInventoriesStatus === 'pending' ? (
          <LoadingSpinner />
        ) : (
          <>
            <InventoriesTable inventories={inventories} onRefresh={handleRefresh} />
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default Inventories;
